import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { routeTree } from "./routeTree.gen";
import { TenantContextProvider } from "./lib/tenant-context";
import { DensityProvider } from "./hooks/use-density";
import { initPostHog } from "./lib/posthog";
import { initSentry } from "./lib/sentry";
import "./styles.css";

// PostHog — activated when VITE_POSTHOG_API_KEY is set
initPostHog();

// Sentry — activated when VITE_SENTRY_DSN is set
initSentry();

// Global error handlers — surface full error details to console for debugging
if (typeof window !== "undefined") {
  window.addEventListener("error", (e) => {
    // eslint-disable-next-line no-console
    console.error("[DIKDOC global error]", e.error || e.message, e.filename + ":" + e.lineno + ":" + e.colno);
  });
  window.addEventListener("unhandledrejection", (e) => {
    // eslint-disable-next-line no-console
    console.error("[DIKDOC unhandled rejection]", e.reason);
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  ReactDOM.createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <TenantContextProvider>
          <DensityProvider>
            <RouterProvider router={router} />
          </DensityProvider>
        </TenantContextProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}
